/* USAGE 
Example:
            <TextButton
              icon={faGraduationCap}
              text="cursuri"
              type="white"
              subMenuItems={[
                { title: "Descopera cursuri", onClick: () => {} },
                { title: "Cursurile mele", onClick: () => {} },
                { title: "Cursuri scoala", onClick: () => {} }
              ]}
            />

Props:
    "text" - the text to be displayed - REQUIRED
    "icon" - the FontAwesome icon - OPTIONAL
    "type" - The type of the button - REQUIRED at least one type: "white" or "primary" or "secondary"
    "className" - Allow additional classes to be passed to the component - OPTIONAL
    "subMenuItems" - Array of objects that appear as a submenu with the following properties:
        "title" - The text of the item - REQUIRED
        "onClick" - Allows onClick actions - OPTIONAL
    "onClick" - Function to be called when the button is clicked - OPTIONAL
*/

import { IconName } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import clsx from "clsx";
import Link from "next/link";
import { UrlObject } from "url";
import { useRouter } from "next/router";

interface Props {
  text: string;
  type?: "white" | "primary" | "secondary";
  href?: string | UrlObject;
  icon?: IconName;
  className?: string;
  active?: boolean;
  onClick?: () => void;
  subMenuItems?: { title: string; url: string | UrlObject; active?: boolean }[];
  simpleLink?: boolean;
  role?: string;
}

export const WLink = ({
  active = false,
  type = "primary",
  ...props
}: Props) => {
  const [openSubmenu, setOpenSubmenu] = useState(false);

  const router = useRouter();

  const isOpen = props?.subMenuItems?.some(
    (item) => item.url === router?.pathname
  );

  const shouldBeForcedOpenSubmenu = useMemo(
    () => (isOpen ? isOpen : openSubmenu),
    [isOpen, openSubmenu]
  );

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      const isNotInList = props?.subMenuItems?.some((item) => item.url === url);

      if (!isNotInList) {
        setOpenSubmenu(false);
      }
    };

    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [props?.subMenuItems, router]);

  if (props?.simpleLink) {
    return (
      <Link
        href={props.href || ""}
        passHref
        role={props.role}
        className={clsx([
          "font-bold text-with-icon-btn cursor-pointer",
          type === "white" && !active && "text-eklight",
          type === "primary" && !active && "text-ekgreen",
          type === "secondary" && !active && "text-ekblue",
          active ? "text-ekgreen" : "text-ekblue lg:text-eklight",
          props?.className,
        ])}
        onClick={(e) => {
          if (!props.href) {
            e.preventDefault();
          }
          if (props.onClick) {
            props.onClick();
          }
        }}
      >
        {props.icon && (
          <FontAwesomeIcon icon={props.icon as IconName} className="w-4 mr-3" />
        )}
        <span>{props.text}</span>
      </Link>
    );
  }

  return (
    <>
      {/*
       * Due to a bug in Link module, eslint will throw warnings
       * if using <a> with no href. So, either use random href
       * on the <a> and add passHref on Link component, or just
       * disable the eslint check for the next line using
       * eslint-disable-next-line jsx-a11y/anchor-is-valid
       *
       * @see https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/main/docs/rules/anchor-is-valid.md
       */}
      <Link
        role={props.role}
        href={props.href || "#"}
        passHref
        className={clsx([
          "font-bold text-with-icon-btn",
          type === "white" && !active && "text-eklight",
          type === "primary" && !active && "text-ekgreen",
          type === "secondary" && !active && "text-ekblue",
          active ? "text-ekgreen" : "text-ekblue lg:text-eklight",
          props?.className,
        ])}
        onClick={
          props.subMenuItems
            ? () => setOpenSubmenu(!openSubmenu)
            : props.onClick
        }
      >
        {props.icon && (
          <FontAwesomeIcon icon={props.icon as IconName} className="w-4 mr-3" />
        )}
        <span>{props.text}</span>
      </Link>
      <AnimatePresence>
        {props.subMenuItems &&
          shouldBeForcedOpenSubmenu &&
          props.subMenuItems.map((item, index) => (
            <motion.span
              key={index}
              initial={{
                opacity: 0,
                x: -10,
              }}
              animate={{
                opacity: 1,
                x: 0,
              }}
              transition={{ duration: 0.15 }}
            >
              <Link
                role={props.role}
                href={item.url}
                passHref
                className={clsx([
                  "cursor-pointer pl-8 hover:text-ekgreen transition-all text-sm",
                  type === "white" && !item.active && "text-eklight",
                  type === "primary" && !item.active && "text-ekgreen",
                  type === "secondary" && !item.active && "text-ekblue",
                  item?.active ? "text-ekgreen" : "text-ekblue lg:text-eklight",
                ])}
              >
                {item.title}
              </Link>
            </motion.span>
          ))}
      </AnimatePresence>
    </>
  );
};

export default WLink;
